<template>
  <div>
    <div v-if="isLoadingThemes && !themesToStudy.length" class="tab">
      <div class="tab__header">
        <div class="tab__title h2">Загрузка…</div>
      </div>
    </div>

    <div v-else-if="loadError" class="tab">
      <div class="tab__header">
        <router-link :to="{ name: 'marathon-session', params: { uuid } }" class="come-back">
          К марафону
        </router-link>
        <div class="tab__title h2">Ошибка загрузки</div>
      </div>
      <div class="tab__content">
        <p>Не удалось загрузить данные. Попробуйте вернуться и открыть снова.</p>
        <router-link :to="{ name: 'marathon-session', params: { uuid } }" class="btn btn-green">
          К марафону
        </router-link>
      </div>
    </div>

    <div v-else class="tab">
      <div class="tab__header">
        <router-link :to="{ name: 'marathon-session', params: { uuid } }" class="come-back">
          К марафону
        </router-link>
        <div class="tab__title h2">Темы с ошибками</div>
      </div>

      <div class="themes-filter">
        <button
          class="themes-filter__btn"
          :class="{ 'themes-filter__btn--active': onlyUnsolved }"
          @click="setFilter(true)"
        >
          Не исправленные
        </button>
        <button
          class="themes-filter__btn"
          :class="{ 'themes-filter__btn--active': !onlyUnsolved }"
          @click="setFilter(false)"
        >
          Все ошибки
        </button>
      </div>

      <div v-if="isLoadingThemes" class="themes-loading">
        <span>Загрузка…</span>
      </div>

      <div v-else-if="!themesToStudy.length" class="themes-empty">
        <div v-if="onlyUnsolved">
          <div class="h3">Все ошибки исправлены!</div>
          <p>Отличная работа — в активных темах не осталось неверных ответов.</p>
        </div>
        <div v-else>
          <div class="h3">Нет ошибок</div>
          <p>В этой сессии пока нет вопросов с ошибками.</p>
        </div>
      </div>

      <div v-else class="themes-table-wrap">
        <table class="themes-table">
          <thead>
            <tr>
              <th>Тема</th>
              <th class="themes-table__col-errors">Ошибок ({{ totalWrongCount }})</th>
              <th class="themes-table__col-action"></th>
              <th class="themes-table__col-action"></th>
              <th v-if="aiEnabled && onlyUnsolved" class="themes-table__col-action"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in themesToStudy" :key="item.theme_id">
              <td>{{ item.theme_name }}</td>
              <td class="themes-table__col-errors">{{ item.wrong_count }}</td>
              <td class="themes-table__col-action">
                <a v-if="item.book_slug" :href="'/' + item.book_slug" target="_blank" class="themes-table__link">Справочник</a>
              </td>
              <td class="themes-table__col-action">
                <a :href="trainerUrl(item.theme_id)" target="_blank" class="themes-table__link">Тренажёр</a>
              </td>
              <td v-if="aiEnabled && onlyUnsolved" class="themes-table__col-action">
                <button class="themes-table__ai-btn" @click="fetchAiExplain(item)">
                  AI Разбор
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <b-modal
      ref="ai-modal"
      hide-footer
      hide-header
      no-close-on-backdrop
      centered
      modal-class="ai-explain-modal"
    >
      <div class="ai-explain-modal__header">
        <div class="ai-explain-modal__title">{{ aiModalTitle }}</div>
        <button class="ai-explain-modal__close" @click="closeAiModal" aria-label="Закрыть">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <div v-if="aiModalLoading" class="ai-explain-loading">
        <div class="ai-explain-spinner"></div>
        <div class="ai-explain-loading-text">Анализируем ошибки с помощью AI…</div>
      </div>

      <div v-else-if="aiModalItems.length" class="ai-explain-list">
        <div v-for="(item, idx) in aiModalItems" :key="idx" class="ai-explain-item">
          <div v-if="item.image" class="ai-explain-item__image">
            <img :src="`${$APP_URL}/storage/${item.image}`" alt="" />
          </div>
          <div class="ai-explain-item__question">{{ item.question_text }}</div>
          <ul v-if="item.answers && item.answers.length" class="ai-explain-answers">
            <li
              v-for="ans in item.answers"
              :key="ans.key"
              class="ai-explain-answers__item"
              :class="{
                'ai-explain-answers__item--correct': ans.correct,
                'ai-explain-answers__item--wrong': ans.user_pick,
              }"
            >
              <span class="ai-explain-answers__icon">
                <svg v-if="ans.correct" width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1.5 6.5L5 10L11.5 3" stroke="#2e7d32" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <svg v-else-if="ans.user_pick" width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 2L11 11M11 2L2 11" stroke="#c62828" stroke-width="1.8" stroke-linecap="round"/></svg>
                <span v-else class="ai-explain-answers__dot"></span>
              </span>
              {{ ans.text }}
            </li>
          </ul>
          <div v-if="item.comment" class="ai-explain-item__comment">
            <span class="ai-explain-item__comment-label">AI разбор</span>
            {{ item.comment }}
          </div>
        </div>
      </div>

      <div v-else class="ai-explain-error">{{ aiModalError }}</div>
    </b-modal>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import api from '@/services/index';

  export default {
    name: 'MarathonThemes',
    data() {
      return {
        loadError: false,
        onlyUnsolved: true,
        aiCache: {},
        aiModalTitle: '',
        aiModalLoading: false,
        aiModalItems: [],
        aiModalError: '',
      };
    },
    computed: {
      ...mapGetters('marathon', [
        'themesToStudy',
        'isLoadingThemes',
        'session',
      ]),
      uuid() {
        return this.$route.params.uuid;
      },
      categoryId() {
        return this.session && this.session.category_id ? this.session.category_id : null;
      },
      aiEnabled() {
        return !!(this.session && this.session.ai_explain_enabled);
      },
      totalWrongCount() {
        return this.themesToStudy.reduce((sum, item) => sum + (item.wrong_count || 0), 0);
      },
    },
    methods: {
      ...mapActions('marathon', ['loadThemesToStudy', 'loadSession']),
      trainerUrl(themeId) {
        const base = `/pdd/themes/${themeId}`;
        return this.categoryId ? `${base}?coll=${this.categoryId}` : base;
      },
      async fetchThemes() {
        this.loadError = false;
        try {
          if (!this.session) {
            await this.loadSession({ uuid: this.uuid });
          }
          await this.loadThemesToStudy({ uuid: this.uuid, onlyUnsolved: this.onlyUnsolved });
        } catch (e) {
          this.loadError = true;
        }
      },
      async setFilter(onlyUnsolved) {
        if (this.onlyUnsolved === onlyUnsolved) return;
        this.onlyUnsolved = onlyUnsolved;
        this.aiCache = {};
        await this.loadThemesToStudy({ uuid: this.uuid, onlyUnsolved });
      },
      async fetchAiExplain(item) {
        const { theme_id: themeId, theme_name: themeName } = item;

        this.aiModalTitle = themeName;
        this.aiModalError = '';

        if (this.aiCache[themeId]) {
          this.aiModalLoading = false;
          this.aiModalItems = this.aiCache[themeId];
          this.$refs['ai-modal'].show();
          return;
        }

        this.aiModalLoading = true;
        this.aiModalItems = [];
        this.$refs['ai-modal'].show();

        try {
          const resp = await api.post(`/marathon/sessions/${this.uuid}/themes/${themeId}/ai-explain`);
          const items = resp.data.items || [];
          this.aiCache[themeId] = items;
          this.aiModalItems = items;
        } catch (e) {
          this.aiModalError = (e.response && e.response.data && e.response.data.message) || 'Ошибка при запросе к AI';
        } finally {
          this.aiModalLoading = false;
        }
      },
      closeAiModal() {
        this.$refs['ai-modal'].hide();
      },
    },
    created() {
      this.fetchThemes();
    },
  };
</script>

<style scoped>
.themes-filter {
  display: inline-flex;
  background: #f2f2f2;
  border-radius: 8px;
  padding: 3px;
  margin: 0 0 16px;
  gap: 2px;
}
.themes-filter__btn {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: #666;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}
.themes-filter__btn:hover:not(.themes-filter__btn--active) {
  background: #e8e8e8;
  color: #333;
}
.themes-filter__btn--active {
  background: #fff;
  color: #1a1a1a;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}
.themes-loading {
  padding: 24px 0;
  color: #999;
  font-size: 14px;
}

.themes-table-wrap {
  padding-right: 26px;
  margin-top: 16px;
  @media screen and (max-width: 992px) {
    padding-right: 15px;
  }
}
.themes-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.themes-table th,
.themes-table td {
  padding: 8px 8px;
  border-bottom: 1px solid #eee;
  text-align: left;
  vertical-align: top;
}
.themes-table tbody tr:hover td {
  background: #f5f5f5;
}
.themes-table th {
  font-weight: 600;
  color: #555;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.themes-table__col-errors {
  text-align: right;
  white-space: nowrap;
  color: #e53935;
  font-weight: 600;
  width: 80px;
}
.themes-table__col-action {
  white-space: nowrap;
  text-align: center;
  width: 1px;
}
.themes-table__link {
  color: #1976d2;
  text-decoration: none;
  font-size: 13px;
}
.themes-table__link:hover {
  text-decoration: underline;
}
.themes-table__ai-btn {
  display: inline-block;
  margin-left: 12px;
  padding: 2px 8px;
  font-size: 12px;
  color: #fff;
  background: #7b1fa2;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  line-height: 20px;
  vertical-align: middle;
}
.themes-table__ai-btn:hover:not(:disabled) {
  background: #6a1b9a;
}
.themes-table__ai-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.themes-empty {
  padding: 48px 16px;
  text-align: center;
  color: #555;
  width: 100%;
}
.themes-empty .h3 {
  margin-bottom: 8px;
}
.themes-empty p {
  margin: 0;
  font-size: 14px;
}
</style>

<style>
.ai-explain-modal .modal-dialog {
  max-width: 640px;
}
.ai-explain-modal .modal-content {
  border-radius: 10px;
  border: none;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14);
}
.ai-explain-modal .modal-body {
  padding: 0;
  text-align: left;
}
.ai-explain-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
}
.ai-explain-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
  text-align: left;
}
.ai-explain-modal__close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 6px;
  color: #999;
  cursor: pointer;
  margin-top: 1px;
  transition: background 0.15s, color 0.15s;
}
.ai-explain-modal__close:hover {
  background: #f5f5f5;
  color: #333;
}
.ai-explain-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px;
}
.ai-explain-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #ede0f5;
  border-top-color: #7b1fa2;
  border-radius: 50%;
  animation: ai-spin 0.8s linear infinite;
}
@keyframes ai-spin {
  to { transform: rotate(360deg); }
}
.ai-explain-loading-text {
  margin-top: 14px;
  font-size: 14px;
  color: #888;
}
.ai-explain-list {
  max-height: 62vh;
  overflow-y: auto;
  padding: 0 24px;
}
.ai-explain-item {
  padding: 16px 0;
  border-bottom: 1px solid #f2f2f2;
}
.ai-explain-item:last-child {
  border-bottom: none;
  padding-bottom: 20px;
}
.ai-explain-item__image {
  margin-bottom: 10px;
  text-align: center;
  background: #f8f8f8;
  border-radius: 6px;
  overflow: hidden;
}
.ai-explain-item__image img {
  max-width: 100%;
  max-height: 220px;
  display: block;
  margin: 0 auto;
}
.ai-explain-item__question {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.5;
  text-align: left;
}
.ai-explain-answers {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  text-align: left;
}
.ai-explain-answers__item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 5px;
  font-size: 13.5px;
  color: #444;
  line-height: 1.45;
  margin-bottom: 2px;
  text-align: left;
}
.ai-explain-answers__item--correct {
  background: #f0faf0;
  color: #1b5e20;
  font-weight: 500;
}
.ai-explain-answers__item--wrong {
  background: #fff5f5;
  color: #b71c1c;
}
.ai-explain-answers__icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.ai-explain-answers__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #ccc;
  display: block;
}
.ai-explain-item__comment {
  margin-top: 10px;
  padding: 10px 12px;
  background: #f7f2fc;
  border-left: 3px solid #9c27b0;
  border-radius: 0 6px 6px 0;
  font-size: 13.5px;
  line-height: 1.65;
  color: #2d2d2d;
  text-align: left;
}
.ai-explain-item__comment-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9c27b0;
  margin-bottom: 4px;
  text-align: left;
}
.ai-explain-error {
  padding: 24px;
  color: #c62828;
  font-size: 14px;
}
</style>
